import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  runTransaction,
  query,
  collection,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from './firebase';
import { generateRoomCode } from '../utils/generateCode';

/**
 * Rebus Session Service
 * Handles all rebus session-related Firestore operations
 */

/**
 * Creates a new rebus session
 * @param {string} rebusId - ID of the rebus to use
 * @returns {Promise<Object>} Created session with room code
 */
export const createRebusSession = async (rebusId) => {
  try {
    // Generate unique room code with collision check
    let roomCode;
    let sessionRef;
    const maxAttempts = 10;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      roomCode = generateRoomCode();
      const q = query(collection(db, 'rebusSessions'), where('id', '==', roomCode));
      const existing = await getDocs(q);
      if (existing.empty) break;
      if (attempt === maxAttempts - 1) {
        throw new Error('Klarte ikkje generere unik PIN-kode. Prøv igjen.');
      }
    }
    sessionRef = doc(db, 'rebusSessions', roomCode);

    const session = {
      id: roomCode,
      rebusId: rebusId,
      status: 'waiting',
      createdAt: new Date().toISOString(),
      startedAt: null,
      finishedAt: null,
      stationLeaders: {},
      teams: {}
    };

    await setDoc(sessionRef, session);

    return session;
  } catch (error) {
    console.error('Error creating rebus session:', error);
    throw new Error(`Klarte ikkje opprette rebus-økt: ${error.message}`);
  }
};

/**
 * Starts a rebus session
 * @param {string} sessionId - Session ID (room code)
 * @returns {Promise<void>}
 */
export const startRebusSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'rebusSessions', sessionId);

    await updateDoc(sessionRef, {
      status: 'active',
      startedAt: new Date().toISOString()
    });

  } catch (error) {
    console.error('Error starting rebus session:', error);
    throw new Error(`Klarte ikkje starte rebus-økt: ${error.message}`);
  }
};

/**
 * Ends a rebus session
 * @param {string} sessionId - Session ID (room code)
 * @returns {Promise<void>}
 */
export const endRebusSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'rebusSessions', sessionId);

    await updateDoc(sessionRef, {
      status: 'finished',
      finishedAt: new Date().toISOString()
    });

  } catch (error) {
    console.error('Error ending rebus session:', error);
    throw new Error(`Klarte ikkje avslutte rebus-økt: ${error.message}`);
  }
};

/**
 * Fetches a rebus session by room code
 * @param {string} sessionId - Session ID (room code)
 * @returns {Promise<Object>} Session data
 */
export const getRebusSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'rebusSessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje rebus-økta');
    }

    return sessionSnap.data();
  } catch (error) {
    console.error('Error fetching rebus session:', error);
    throw new Error(`Klarte ikkje hente rebus-økt: ${error.message}`);
  }
};

/**
 * Delete a rebus session permanently.
 * @param {string} sessionId - Session ID (room code)
 * @returns {Promise<void>}
 */
export const deleteRebusSession = async (sessionId) => {
  const sessionRef = doc(db, 'rebusSessions', sessionId);
  await deleteDoc(sessionRef);
};

/**
 * Score a team's challenge at a station (station leader scores)
 * @param {string} sessionId - Session ID (room code)
 * @param {string} teamId - Team ID
 * @param {string} stationId - Station ID
 * @param {number} score - Challenge score awarded
 * @param {string} scorerName - Name of the station leader scoring
 * @returns {Promise<void>}
 */
export const scoreChallenge = async (sessionId, teamId, stationId, score, scorerName) => {
  try {
    if (score < 0) {
      throw new Error('Poeng kan ikkje vere negative');
    }

    const sessionRef = doc(db, 'rebusSessions', sessionId);

    await runTransaction(db, async (transaction) => {
      const sessionSnap = await transaction.get(sessionRef);

      if (!sessionSnap.exists()) {
        throw new Error('Fann ikkje rebus-økta');
      }

      const sessionData = sessionSnap.data();

      if (!sessionData.teams || !sessionData.teams[teamId]) {
        throw new Error('Fann ikkje laget i økta');
      }

      transaction.update(sessionRef, {
        [`teams.${teamId}.stationResults.${stationId}.challengeScore`]: score,
        [`teams.${teamId}.stationResults.${stationId}.challengeScoredBy`]: scorerName,
        [`teams.${teamId}.stationResults.${stationId}.challengeScoredAt`]: new Date().toISOString()
      });
    });

  } catch (error) {
    console.error('Error scoring challenge:', error);
    throw new Error(`Klarte ikkje gje poeng for utfordring: ${error.message}`);
  }
};

/**
 * Approve or reject a team's extra task submission
 * @param {string} sessionId - Session ID (room code)
 * @param {string} teamId - Team ID
 * @param {string} taskId - Extra task ID
 * @param {boolean} approved - Whether the task is approved
 * @param {number} points - Points awarded (0 if rejected)
 * @returns {Promise<void>}
 */
export const approveExtraTask = async (sessionId, teamId, taskId, approved, points, adminFeedback = '') => {
  try {
    const sessionRef = doc(db, 'rebusSessions', sessionId);

    await runTransaction(db, async (transaction) => {
      const sessionSnap = await transaction.get(sessionRef);

      if (!sessionSnap.exists()) {
        throw new Error('Fann ikkje rebus-økta');
      }

      const sessionData = sessionSnap.data();

      if (!sessionData.teams || !sessionData.teams[teamId]) {
        throw new Error('Fann ikkje laget i økta');
      }

      if (!sessionData.teams[teamId].extraTaskResults ||
          !sessionData.teams[teamId].extraTaskResults[taskId]) {
        throw new Error('Fann ikkje innleveringa for denne oppgåva');
      }

      const updateData = {
        [`teams.${teamId}.extraTaskResults.${taskId}.approved`]: approved,
        [`teams.${teamId}.extraTaskResults.${taskId}.points`]: approved ? points : 0,
        [`teams.${teamId}.extraTaskResults.${taskId}.reviewedAt`]: new Date().toISOString()
      };

      if (adminFeedback && adminFeedback.trim()) {
        updateData[`teams.${teamId}.extraTaskResults.${taskId}.adminFeedback`] = adminFeedback.trim();
      }

      transaction.update(sessionRef, updateData);
    });

  } catch (error) {
    console.error('Error approving extra task:', error);
    throw new Error(`Klarte ikkje vurdere ekstraoppgåve: ${error.message}`);
  }
};

/**
 * Approve or reject a team's station answer submission
 * @param {string} sessionId - Session ID (room code)
 * @param {string} teamId - Team ID
 * @param {string} stationId - Station ID
 * @param {string} questionId - Question ID
 * @param {boolean} approved - Whether the answer is approved
 * @param {number} points - Points awarded (0 if rejected)
 * @param {string} adminFeedback - Optional feedback text
 * @returns {Promise<void>}
 */
export const approveStationAnswer = async (sessionId, teamId, stationId, questionId, approved, points, adminFeedback = '') => {
  try {
    const sessionRef = doc(db, 'rebusSessions', sessionId);

    await runTransaction(db, async (transaction) => {
      const sessionSnap = await transaction.get(sessionRef);

      if (!sessionSnap.exists()) {
        throw new Error('Fann ikkje rebus-økta');
      }

      const sessionData = sessionSnap.data();

      if (!sessionData.teams || !sessionData.teams[teamId]) {
        throw new Error('Fann ikkje laget i økta');
      }

      const stationResult = sessionData.teams[teamId].stationResults?.[stationId];
      if (!stationResult?.answers?.[questionId]) {
        throw new Error('Fann ikkje svaret for dette spørsmålet');
      }

      const updateData = {
        [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.approved`]: approved,
        [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.points`]: approved ? points : 0,
        [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.isCorrect`]: approved,
        [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.reviewedAt`]: new Date().toISOString(),
      };

      if (adminFeedback && adminFeedback.trim()) {
        updateData[`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.adminFeedback`] = adminFeedback.trim();
      }

      transaction.update(sessionRef, updateData);
    });

  } catch (error) {
    console.error('Error approving station answer:', error);
    throw new Error(`Klarte ikkje vurdere stasjonssvar: ${error.message}`);
  }
};

/**
 * Assign bonus (or penalty) points to a team
 * @param {string} sessionId - Session ID (room code)
 * @param {string} teamId - Team ID
 * @param {number} points - Points to add (negative for penalty)
 * @param {string} [reason] - Optional reason for the bonus
 * @returns {Promise<void>}
 */
export const assignBonusPoints = async (sessionId, teamId, points, reason = '') => {
  try {
    if (!points || typeof points !== 'number') {
      throw new Error('Ugyldig poengverdi');
    }

    const sessionRef = doc(db, 'rebusSessions', sessionId);

    await runTransaction(db, async (transaction) => {
      const sessionSnap = await transaction.get(sessionRef);

      if (!sessionSnap.exists()) {
        throw new Error('Fann ikkje Mikkelchase-økta');
      }

      const sessionData = sessionSnap.data();

      if (!sessionData.teams || !sessionData.teams[teamId]) {
        throw new Error('Fann ikkje laget i økta');
      }

      const currentBonus = sessionData.teams[teamId].bonusPoints || 0;
      const currentHistory = sessionData.teams[teamId].bonusHistory || [];

      const entry = {
        points,
        reason: reason.trim(),
        assignedAt: new Date().toISOString(),
      };

      transaction.update(sessionRef, {
        [`teams.${teamId}.bonusPoints`]: currentBonus + points,
        [`teams.${teamId}.bonusHistory`]: [...currentHistory, entry],
      });
    });

  } catch (error) {
    console.error('Error assigning bonus points:', error);
    throw new Error(`Klarte ikkje gi bonuspoeng: ${error.message}`);
  }
};

/**
 * Reset an extra task approval (back to pending)
 */
export const resetExtraTaskApproval = async (sessionId, teamId, taskId) => {
  const sessionRef = doc(db, 'rebusSessions', sessionId);
  await updateDoc(sessionRef, {
    [`teams.${teamId}.extraTaskResults.${taskId}.approved`]: null,
    [`teams.${teamId}.extraTaskResults.${taskId}.points`]: 0,
    [`teams.${teamId}.extraTaskResults.${taskId}.reviewedAt`]: null,
    [`teams.${teamId}.extraTaskResults.${taskId}.adminFeedback`]: null,
  });
};

/**
 * Reset a station answer approval (back to pending)
 */
export const resetStationAnswerApproval = async (sessionId, teamId, stationId, questionId) => {
  const sessionRef = doc(db, 'rebusSessions', sessionId);
  await updateDoc(sessionRef, {
    [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.approved`]: null,
    [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.points`]: 0,
    [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.isCorrect`]: null,
    [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.reviewedAt`]: null,
    [`teams.${teamId}.stationResults.${stationId}.answers.${questionId}.adminFeedback`]: null,
  });
};

/**
 * Submit a challenge comment/result for a team at a station (used by station leaders)
 * @param {string} sessionId - Session ID
 * @param {string} stationId - Station ID
 * @param {string} teamId - Team ID being commented on
 * @param {string} comment - The challenge result comment (e.g. time, count)
 * @param {string} submittedBy - Name of the station leader
 * @returns {Promise<void>}
 */
export const submitChallengeComment = async (sessionId, stationId, teamId, comment, submittedBy) => {
  try {
    if (!comment?.trim()) {
      throw new Error('Kommentar er påkravd');
    }

    const sessionRef = doc(db, 'rebusSessions', sessionId);
    await updateDoc(sessionRef, {
      [`teams.${teamId}.stationResults.${stationId}.challengeComment`]: comment.trim(),
      [`teams.${teamId}.stationResults.${stationId}.challengeCommentBy`]: submittedBy,
      [`teams.${teamId}.stationResults.${stationId}.challengeCommentAt`]: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error submitting challenge comment:', error);
    throw new Error(`Klarte ikkje lagre kommentar: ${error.message}`);
  }
};

/**
 * Set start station for a team (rotates station order for that team)
 * @param {string} sessionId - Session ID (room code)
 * @param {string} teamId - Team ID
 * @param {string} stationId - Station ID to start at
 * @returns {Promise<void>}
 */
export const setTeamStartStation = async (sessionId, teamId, stationId) => {
  try {
    const sessionRef = doc(db, 'rebusSessions', sessionId);
    await updateDoc(sessionRef, {
      [`teams.${teamId}.startStationId`]: stationId
    });
  } catch (error) {
    console.error('Error setting start station:', error);
    throw new Error(`Klarte ikkje setje startstasjon: ${error.message}`);
  }
};

/**
 * Add a reaction emoji to a photo submission
 * @param {string} sessionId - Session ID
 * @param {string} teamId - Team who submitted the photo
 * @param {string} taskId - Extra task ID
 * @param {string} emoji - Emoji character
 * @returns {Promise<void>}
 */
export const addReaction = async (sessionId, teamId, taskId, emoji) => {
  try {
    const sessionRef = doc(db, 'rebusSessions', sessionId);
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(sessionRef);
      if (!snap.exists()) throw new Error('Fann ikkje økta');
      const data = snap.data();
      const currentReactions = data.teams?.[teamId]?.extraTaskResults?.[taskId]?.reactions || {};
      const currentCount = currentReactions[emoji] || 0;
      transaction.update(sessionRef, {
        [`teams.${teamId}.extraTaskResults.${taskId}.reactions.${emoji}`]: currentCount + 1,
      });
    });
  } catch (error) {
    console.error('Error adding reaction:', error);
  }
};

/**
 * Register a station leader for a specific station
 * @param {string} sessionId - Session ID (room code)
 * @param {string} stationId - Station ID
 * @param {string} leaderName - Station leader's name
 * @returns {Promise<void>}
 */
export const joinAsStationLeader = async (sessionId, stationId, leaderName) => {
  try {
    if (!leaderName || typeof leaderName !== 'string' || leaderName.trim().length < 2) {
      throw new Error('Ugyldig namn. Må vere minst 2 teikn.');
    }

    const sessionRef = doc(db, 'rebusSessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      throw new Error('Fann ikkje rebus-økta');
    }

    await updateDoc(sessionRef, {
      [`stationLeaders.${stationId}`]: {
        name: leaderName.trim(),
        joinedAt: new Date().toISOString()
      }
    });

  } catch (error) {
    console.error('Error joining as station leader:', error);
    throw new Error(`Klarte ikkje registrere stasjonsleiar: ${error.message}`);
  }
};
